import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type { CustomerNoteType } from "./customerNoteTypesApi";
import {
  addCustomerNoteType,
  deleteCustomerNoteType,
  listCustomerNoteTypes,
  updateCustomerNoteTypeName,
} from "./customerNoteTypesApi";

const customerNoteTypesKey = "customer-note-types-dictionary";

export const useCustomerNoteTypes = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [customerNoteTypesKey],
    queryFn: ({ signal }) => listCustomerNoteTypes(signal),
  });

  const addMutation = useMutation<CustomerNoteType, unknown, string>({
    mutationFn: (name) => addCustomerNoteType(name),
    onSuccess: async (data) => {
      enqueueSnackbar(`Typ notatki '${data.name}' został dodany do słownika`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [customerNoteTypesKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać typu notatki do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<CustomerNoteType, unknown, { id: string; name: string }>({
    mutationFn: ({ id, name }) => updateCustomerNoteTypeName(id, name),
    onSuccess: async (data) => {
      enqueueSnackbar(`Nazwa typu notatki została zmieniona na '${data.name}'`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [customerNoteTypesKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zmienić nazwy typu notatki. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<void, unknown, string>({
    mutationFn: (id) => deleteCustomerNoteType(id),
    onSuccess: async () => {
      enqueueSnackbar("Typ notatki został usunięty ze słownika", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [customerNoteTypesKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć typu notatki ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    noteTypes: sortBy(data ?? [], ({ name }) => name.trim().toLowerCase()),
    isLoading,
    addNoteType: (name: string) => addMutation.mutateAsync(name),
    updateNoteTypeName: (id: string, name: string) => updateMutation.mutateAsync({ id, name }),
    deleteNoteType: (id: string) => deleteMutation.mutateAsync(id),
  };
};
